import type { ICustomerRef } from '@team-milva/lab-models/customer'
import type {
  ApiResult,
  HttpMethod,
  PathParams,
  PathsWithMethod,
  RequestOptions,
} from '@team-milva/lab-utils/apiClient'
import type { paths } from './codegen/customer.types'
import { customerApi } from './customerClient'

/**
 * Customer-scoped lab-cloud endpoints are mounted once per owner type (MIL-402):
 *
 * - `lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/…` — a business customer
 * - `lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/…` — an
 *   insurance customer
 *
 * Both trees serve the same schemas, so every store builds its API path through this one helper
 * from the signed-in user's `customer` rather than spelling out `customer-accounts/…` itself.
 * Call sites are typed against the business path; the helper swaps in the insurance tree at runtime.
 */
const BUSINESS_PREFIX = 'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}'
const INSURANCE_PREFIX =
  'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}'

type TBusinessPath<S extends string> = `${typeof BUSINESS_PREFIX}${S}`
type TInsurancePath<S extends string> = `${typeof INSURANCE_PREFIX}${S}`

/** Every path suffix served under both owner trees, e.g. `/orders/draft`. */
export type TCustomerOwnerSuffix = {
  [P in keyof paths]: P extends TBusinessPath<infer S>
    ? TInsurancePath<S> extends keyof paths
      ? S
      : never
    : never
}[keyof paths]

type TOwnerPath<M extends HttpMethod, S extends TCustomerOwnerSuffix> = TBusinessPath<S> &
  PathsWithMethod<paths, M>

/** The path params a caller still supplies — everything but the lab and the owner. */
type TOwnerPathParams<M extends HttpMethod, S extends TCustomerOwnerSuffix> = Omit<
  PathParams<paths, TOwnerPath<M, S>, M>,
  'labAccountID' | 'customerAccountID'
>

export type TCustomerOwnerRequestOptions<
  M extends HttpMethod,
  S extends TCustomerOwnerSuffix,
> = Omit<RequestOptions<paths, TOwnerPath<M, S>, M>, 'path'> &
  (keyof TOwnerPathParams<M, S> extends never
    ? { path?: never }
    : { path: TOwnerPathParams<M, S> })

/** Who a customer-scoped request is made for: the lab and the business or insurance customer. */
export interface ICustomerOwnerContext {
  labAccountID: string
  customer: ICustomerRef
}

/**
 * The owner context of a signed-in customer-app user, or `null` for anyone who does not belong to
 * a business or an insurance customer (a private customer has no customer-scoped endpoints).
 */
export function customerOwnerContext(
  user: { labAccountID: string; customer?: ICustomerRef | null } | null | undefined
): ICustomerOwnerContext | null {
  const customer = user?.customer
  if (!user || !customer || !customer.id) return null
  if (customer.type !== 'business' && customer.type !== 'insurance') return null
  return { labAccountID: user.labAccountID, customer }
}

/** The concrete path for `owner` — exported for tests and for logging. */
export function customerOwnerPath(owner: ICustomerOwnerContext, suffix: string): string {
  return `${owner.customer.type === 'insurance' ? INSURANCE_PREFIX : BUSINESS_PREFIX}${suffix}`
}

/** The lab and owner path params, under the param name the owner's tree uses. */
export function customerOwnerPathParams(
  owner: ICustomerOwnerContext
): Record<string, string> {
  return owner.customer.type === 'insurance'
    ? { labAccountID: owner.labAccountID, insuranceCustomerAccountID: owner.customer.id }
    : { labAccountID: owner.labAccountID, customerAccountID: owner.customer.id }
}

type TLooseRequest = (
  method: HttpMethod,
  path: string,
  options?: { path?: Record<string, string>; query?: unknown; body?: unknown; headers?: Record<string, string> }
) => Promise<unknown>

/**
 * Calls a customer-scoped endpoint for `owner`, on the business or the insurance route tree.
 * Typed exactly like `customerApi` on the business path (same body, query and result), with the
 * lab and owner path params filled in from `owner`.
 */
export function customerOwnerApi<M extends HttpMethod, S extends TCustomerOwnerSuffix>(
  owner: ICustomerOwnerContext,
  method: M,
  suffix: S,
  options?: TCustomerOwnerRequestOptions<M, S>
): Promise<ApiResult<paths, TOwnerPath<M, S>, M>>
export function customerOwnerApi(
  owner: ICustomerOwnerContext,
  method: HttpMethod,
  suffix: string,
  options?: { path?: Record<string, string>; query?: unknown; body?: unknown; headers?: Record<string, string> }
): Promise<unknown> {
  // The two trees differ only in the owner segment's name, and the client fills `{param}`
  // placeholders by name, so the business-typed call is sent to whichever tree `owner` names.
  // Widened to the client's runtime shape for that one reason.
  const request = customerApi as unknown as TLooseRequest
  return request(method, customerOwnerPath(owner, suffix), {
    ...options,
    path: { ...(options?.path ?? {}), ...customerOwnerPathParams(owner) },
  })
}
